import api from './client';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  DashboardBirthdays,
  UpcomingChanges,
} from '@gokrew/models/company-dashboard';
import type {
  GetDashboardSummaryResponse,
  GetEmployeesOnLeaveResponse,
  GetAssetTypeDistributionResponse,
  GetDashboardAttendanceResponse,
  GetDashboardCardsResponse,
} from '@/types/dashboard';

const dashboardUrl = (companyId: string, path: string) =>
  `/companies/${companyId}/dashboard/${path}`;

const endpoints = {
  summary: 'summary',
  employeesOnLeave: 'employees-on-leave',
  assetTypeDistribution: 'asset-type-distribution',
  attendance: 'attendance',
  cards: 'cards',
  upcomingChanges: 'upcoming-changes',
  birthdays: 'birthdays',
};

// Only include branchId in the query when a branch is selected
const branchParams = (branchId?: string) =>
  branchId ? { params: { branchId } } : undefined;

export async function getDashboardSummary(
  companyId: string,
  branchId?: string
) {
  const { data } = await api.get<GetDashboardSummaryResponse>(
    dashboardUrl(companyId, endpoints.summary),
    branchParams(branchId)
  );
  return data;
}

export async function getEmployeesOnLeave(
  companyId: string,
  branchId?: string
) {
  const { data } = await api.get<GetEmployeesOnLeaveResponse>(
    dashboardUrl(companyId, endpoints.employeesOnLeave),
    branchParams(branchId)
  );
  return data;
}

export async function getAssetTypeDistribution(
  companyId: string,
  branchId?: string
) {
  const { data } = await api.get<GetAssetTypeDistributionResponse>(
    dashboardUrl(companyId, endpoints.assetTypeDistribution),
    branchParams(branchId)
  );
  return data;
}

export async function getCompanyTasksSummary(companyId: string) {
  const { data } = await api.get(
    `/companies/${companyId}/dashboard/company-tasks-summary`
  );
  return data;
}

export async function getDashboardAttendance(
  companyId: string,
  branchId?: string
) {
  const { data } = await api.get<GetDashboardAttendanceResponse>(
    dashboardUrl(companyId, endpoints.attendance),
    branchParams(branchId)
  );
  return data;
}

export async function getDashboardCards(companyId: string, branchId?: string) {
  const { data } = await api.get<GetDashboardCardsResponse>(
    dashboardUrl(companyId, endpoints.cards),
    branchParams(branchId)
  );
  return data;
}

/** People changes in the next 30 days, for the Upcoming changes drawer. */
export async function getUpcomingChanges(companyId: string, branchId?: string) {
  const { data } = await api.get<ApiResponse<UpcomingChanges>>(
    dashboardUrl(companyId, endpoints.upcomingChanges),
    branchParams(branchId)
  );
  return data;
}

/** Birthdays today and in the next six days, for the Birthdays card. */
export async function getDashboardBirthdays(
  companyId: string,
  branchId?: string
) {
  const { data } = await api.get<ApiResponse<DashboardBirthdays>>(
    dashboardUrl(companyId, endpoints.birthdays),
    branchParams(branchId)
  );
  return data;
}
